import { Injectable } from '@angular/core';
import { logFeature, logUserAction } from '@office-start/telemetry-service';
import { Area, DataSecurityLevel as OtelDataSecurityLevel, KnownFeatureNames, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { getReferral } from '@office-start/telemetry-utility';

import { InstrumentationParam, InstrumentationService, KnownAreas, KnownInstrumentationActions, KnownInstrumentationEvents, KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { KnownWorkloadReferralNames } from '../shared/models/known-workload-referral-names.model';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { SearchInstrumentedXhrService } from './search-instrumented-xhr.service';
import { IUsageEvent } from './search-page-bootstrapper/search-page-bootstrapper.service';

export const enum SearchResultType {
    // Search Providers
    All = 'All',
    App = 'App',
    Bing = 'Bing',
    File = 'File',
    Help = 'Help',
    GoTo = 'Go_To',
    Template = 'Template',
    SeeMore = 'See_More',
    Learning = 'Learning',
    Setting = 'Setting',
    Serp = 'Serp',

    // Search Provider Extra Visual Components
    // Used to distinguish specific component clicks for a provider
    LearningPrimary = 'LearningPrimary',
    LearningSecondary = 'LearningSecondary'
}

export const enum SearchResultElementId {
    App = 'MSA_Auto_Suggest_App',
    Bing = 'MSA_Auto_Suggest_Bing',
    File = 'MSA_Auto_Suggest_File',
    Help = 'MSA_Auto_Suggest_Help',
    GoTo = 'MSA_Auto_Suggest_Go_To',
    Template = 'MSA_Auto_Suggest_Template',
    SeeMore = 'MSA_Auto_Suggest_See_More',
    Learning = 'MSA_Auto_Suggest_Learning',
    Setting = 'MSA_Auto_Suggest_Setting',
    Serp = 'MSA_Auto_Suggest_Serp'
}

export const enum SerpRefinerType {
    AllFileTypeRefiner = 'all_FileType',
    FileFileTypeRefiner = 'docs_FileType',
    TemplateFileTypeRefiner = 'templates_TemplateType'
}

export const enum SerpVerticalId {
    All = 'main',
    Template = 'templates',
    File = 'docs'
}

export const enum SerpResultElementId {
    App = 'MSA_Serp_App',
    File = 'MSA_Serp_File',
    Template = 'MSA_Serp_Template'
}

const enum SearchItemCategory {
    DataFetchLatency = 'Data_Fetch_Latency',
    E2ELatency = 'E2E_Latency',
    FilterOpen = 'Filter_Open',
    FilterSubmit = 'Filter_Submit',
    Result = 'Result',
    VerticalChange = 'Vertical_Change'
}

const enum  OtherKnownReferralNames {
    PDF = 'PDF'
}

interface ConsumerSearchInstrumentationEvent {
    /**
     * Usage:
     * SearchClick - any click results to a Search success.
     * SearchQuery - query related information.
     * SearchCapability - any click doesn't directly result to a Search success and other user actions.
     * SearchPerformance - search performance data.
     * SearchRenderedResults - rendered results for each provider.
     * OfficeInsightsSearchSuggestionRequest - search requests to Office Insight Service.
     */
    event: KnownInstrumentationEvents.SearchClick | KnownInstrumentationEvents.SearchQuery | KnownInstrumentationEvents.SearchCapability | KnownInstrumentationEvents.SearchPerformance | KnownInstrumentationEvents.SearchRenderedResults | KnownInstrumentationEvents.OfficeInsightsSearchSuggestionRequest;
    elementId: SerpResultElementId;
    itemCategory: SearchItemCategory;
    knownArea: KnownAreas;
    searchProviderType: SearchResultType;
    searchResultType: SearchResultType | SerpRefinerType;
    pageNav: boolean;
}

interface ContextToEventMapperParameter {
    filterId: string;
    verticalId: string;
}

@Injectable({
    providedIn: 'root'
})
export class SearchInstrumentationService {

    private searchSubmitted: boolean;
    private searchText: string;
    private searchSessionId: string;
    private searchQueryId: number;
    private referralPlatform: KnownReferralPlatform;
    private readonly renderedSearchResults: Map<SearchResultType, Array<string>>;
    private readonly contextToEventMapper: { [context: string]: (parameter?: ContextToEventMapperParameter) => Array<ConsumerSearchInstrumentationEvent>};
    private readonly officeInsightsServiceEndpoint: string = 'https://uci.officeapps.live.com/OfficeInsights/Telemetry/V1';

    constructor(
        private readonly instrumentationService: InstrumentationService,
        private readonly searchInstrumentedXhrService: SearchInstrumentedXhrService
    ) {
        this.renderedSearchResults = new Map<SearchResultType, Array<string>>();
        this.updateSearchSessionId();

        this.contextToEventMapper = {
            'AllSearchPanel.AppSearchResult.onClick': () => (
                [{
                    itemCategory: SearchItemCategory.Result,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType: SearchResultType.All,
                    searchResultType: SearchResultType.App,
                    event: KnownInstrumentationEvents.SearchClick,
                    elementId: SerpResultElementId.App,
                    pageNav: true
                }]
            ),
            'AllSearchPanel.TriageFileSearchResult.onClick': () => (
                [{
                    itemCategory: SearchItemCategory.Result,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType: SearchResultType.All,
                    searchResultType: SearchResultType.File,
                    event: KnownInstrumentationEvents.SearchClick,
                    elementId: SerpResultElementId.File,
                    pageNav: true
                }]
            ),
            'AllSearchPanel.TemplateSearchResult.onClick': () => (
                [{
                    itemCategory: SearchItemCategory.Result,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType: SearchResultType.All,
                    searchResultType: SearchResultType.Template,
                    event: KnownInstrumentationEvents.SearchClick,
                    elementId: SerpResultElementId.Template,
                    pageNav: true
                }]
            ),
            /**
             * File Search Page
             */
            // Logs when the user clicks on a file in the Files tab in the SERP
            'FilesSearchPanel.TriageFileSearchResult.onClick': () => (
                [{
                    itemCategory: SearchItemCategory.Result,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType: SearchResultType.File,
                    searchResultType: SearchResultType.File,
                    event: KnownInstrumentationEvents.SearchClick,
                    elementId: SerpResultElementId.File,
                    pageNav: true
                }]
            ),
            // Logs when the user clicks on a template in the Templates tab in the SERP
            'TemplatesSearchPanel.TemplateSearchResult.onClick': () => (
                [{
                    itemCategory: SearchItemCategory.Result,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType: SearchResultType.Template,
                    searchResultType: SearchResultType.Template,
                    event: KnownInstrumentationEvents.SearchClick,
                    elementId: SerpResultElementId.Template,
                    pageNav: true
                }]
            ),
            /**
             * Template Search Page
             */
            // Logs when the user clicks on a template in the Templates tab in the SERP
            'CustomRefinersContainer.onRefinerClick': (parameter: ContextToEventMapperParameter) => {
                let searchProviderType: SearchResultType;
                let searchResultType: SerpRefinerType;
                switch (parameter.filterId) {
                    case SerpRefinerType.AllFileTypeRefiner:
                        searchProviderType = SearchResultType.All;
                        searchResultType = SerpRefinerType.AllFileTypeRefiner;
                        break;
                    case SerpRefinerType.FileFileTypeRefiner:
                        searchProviderType = SearchResultType.File;
                        searchResultType = SerpRefinerType.FileFileTypeRefiner;
                        break;
                    case SerpRefinerType.TemplateFileTypeRefiner:
                        searchProviderType = SearchResultType.Template;
                        searchResultType = SerpRefinerType.TemplateFileTypeRefiner;
                        break;
                    default:
                        searchProviderType =  null;
                        searchResultType = null;
                }

                return [{
                    itemCategory: SearchItemCategory.FilterOpen,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType,
                    searchResultType,
                    event: KnownInstrumentationEvents.SearchCapability,
                    elementId: null,
                    pageNav: false
                }];
            },
            // Logs when the user submits a filter or multiple filters
            'CustomRefinersContainer.applyRefiner': (parameter: ContextToEventMapperParameter) => {
                let searchProviderType: SearchResultType;
                switch (parameter.filterId) {
                    case SerpRefinerType.AllFileTypeRefiner:
                        searchProviderType = SearchResultType.All;
                        break;
                    case SerpRefinerType.FileFileTypeRefiner:
                        searchProviderType = SearchResultType.File;
                        break;
                    case SerpRefinerType.TemplateFileTypeRefiner:
                        searchProviderType = SearchResultType.Template;
                        break;
                    default:
                        searchProviderType =  null;
                }

                return [{
                    itemCategory: SearchItemCategory.FilterSubmit,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType,
                    searchResultType: null, // TODO: ideally this should be clicked filter(s).
                    event: KnownInstrumentationEvents.SearchCapability,
                    elementId: null,
                    pageNav: false
                }];
            },
            'VerticalFiltersRoot.VerticalFilters.changeAndLog':  (parameter: ContextToEventMapperParameter) => {
                let searchProviderType: SearchResultType;
                switch (parameter.verticalId) {
                    case SerpVerticalId.All:
                        searchProviderType = SearchResultType.All;
                        break;
                    case SerpVerticalId.File:
                        searchProviderType = SearchResultType.File;
                        break;
                    case SerpVerticalId.Template:
                        searchProviderType = SearchResultType.Template;
                        break;
                    default:
                        searchProviderType = null;
                }
                return [{
                    itemCategory: SearchItemCategory.VerticalChange,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType,
                    searchResultType: null,
                    event: KnownInstrumentationEvents.SearchCapability,
                    elementId: null,
                    pageNav: false
                }];
            },
            /**
             * Performance Logging
             */
            // Data Fetching
            'ResultsRendered': (parameter: ContextToEventMapperParameter) => {
                let searchProviderType: SearchResultType;
                switch (parameter.verticalId) {
                    case SerpVerticalId.All:
                        searchProviderType = SearchResultType.All;
                        break;
                    case SerpVerticalId.File:
                        searchProviderType = SearchResultType.File;
                        break;
                    case SerpVerticalId.Template:
                        searchProviderType = SearchResultType.Template;
                        break;
                    default:
                        searchProviderType = null;
                }
                return [{
                    itemCategory: SearchItemCategory.DataFetchLatency,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType,
                    searchResultType: null,
                    event: KnownInstrumentationEvents.SearchPerformance,
                    elementId: null,
                    pageNav: null
                }, {
                    itemCategory: SearchItemCategory.E2ELatency,
                    knownArea: KnownAreas.SearchResults,
                    searchProviderType,
                    searchResultType: null,
                    event: KnownInstrumentationEvents.SearchPerformance,
                    elementId: null,
                    pageNav: null
                }];
            }
        };
    }

    public logSearchClickEvent(providerType: SearchResultType, elementId: string, pageNav: boolean, resultType?: SearchResultType, searchTargetId?: string, referral?: string, securityThreshold?: DataSecurityLevel, position?: string,  referralPlatform?: KnownReferralPlatform) {
        const params = {};

        params[InstrumentationParam.Action] = KnownInstrumentationActions.Click;
        params[InstrumentationParam.SearchProviderType] = providerType;
        params[InstrumentationParam.SearchResultType] = resultType ? resultType : providerType;
        params[InstrumentationParam.SearchTargetId] = searchTargetId;
        params[InstrumentationParam.SearchSessionId] = this.searchSessionId;
        params[InstrumentationParam.Index] = position;

        this.instrumentationService.logClickEvent(
                                        KnownInstrumentationEvents.SearchClick,
                                        elementId,
                                        KnownAreas.SearchSuggestions,
                                        referral,
                                        referralPlatform ? referralPlatform : this.referralPlatform,
                                        params,
                                        undefined /*metrics*/,
                                        securityThreshold,
                                        pageNav);
        // User Click should log through standard UserAction for export purpose
        logUserAction({
            eventName: KnownInstrumentationEvents.SearchClick,
            id: elementId,
            area: KnownAreas.SearchSuggestions as Area,
            result: referral ? 'Refer' : 'Navigate',
            target: referral ? getReferral(referral) : KnownAreas.SearchSuggestions as Area
        });
        // Put Clicks related properties to Feature event for Search team scorcarding
        params[InstrumentationParam.Area] = KnownAreas.SearchSuggestions;
        params[InstrumentationParam.ElementId] = elementId;
        params[InstrumentationParam.Referral] = referral;
        params[InstrumentationParam.ReferralPlatform] = referralPlatform ? referralPlatform : this.referralPlatform;
        logFeature({ featureName: KnownFeatureNames.Search , eventName: KnownInstrumentationEvents.SearchClick, params});
    }

    // Logging searchText length.
    // Query Id starts from 1.
    public logSearchTextInfo(securityThreshold?: DataSecurityLevel) {
        const params = {};

        // this.searchText is a string container.
        // The length property of the container is considered System Metadata and is not a Direct Measurement of customer content so is permitted.
        params[InstrumentationParam.SearchCharacterCount] = this.searchText.length;
        params[InstrumentationParam.SearchSessionId] = this.searchSessionId;
        params[InstrumentationParam.SearchQueryId] = ++this.searchQueryId;

        this.instrumentationService.logGenericEvent(
                                        KnownInstrumentationEvents.SearchQuery,
                                        params,
                                        undefined /*metrics*/,
                                        securityThreshold);
        logFeature({ featureName: KnownFeatureNames.Search , eventName: KnownInstrumentationEvents.SearchQuery, params}, securityThreshold as unknown as OtelDataSecurityLevel);
    }

    public logSearchTextId(securityThreshold?: DataSecurityLevel) {
        this.getSearchTextIdPromise()
            .then(data => {
                this.sentSearchTextLog(true, data, securityThreshold);
            }).catch(() => {
                this.sentSearchTextLog(false, undefined, securityThreshold);
            });
    }

    public logRenderedSearchResults() {
        this.renderedSearchResults.forEach((results, searchProviderType) => {
            if (results.length > 0) {
                const params = {};
                params[InstrumentationParam.SearchSessionId] = this.searchSessionId;
                params[InstrumentationParam.SearchProviderType] = searchProviderType;
                params[InstrumentationParam.SearchResultType] = JSON.stringify(results);
                params[InstrumentationParam.Area] = KnownAreas.SearchResults;
                this.instrumentationService.logGenericEvent(
                                                KnownInstrumentationEvents.SearchRenderedResults,
                                                params);
                logFeature({ featureName: KnownFeatureNames.Search , eventName: KnownInstrumentationEvents.SearchRenderedResults, params});
            }
        });

        this.renderedSearchResults.clear();
    }

    public logSerpEvent(logEvent: IUsageEvent, referral: string = '', referralPlatform?: KnownReferralPlatform) {
        const logProperties = logEvent.properties || {};
        const serpEventFunction = this.contextToEventMapper[logEvent.nameDetail || logEvent.name];
        if (typeof serpEventFunction === 'undefined') {
            return;
        }

        const contextToEventMapperParameter: ContextToEventMapperParameter = {
            filterId: logProperties?.options?.RefinerId,
            verticalId: logProperties?.VerticalId || logProperties?.options?.itemKey
        };
        const serpEventArray = serpEventFunction(contextToEventMapperParameter);
        const params = {};

        serpEventArray.forEach(serpEvent => {
            params[InstrumentationParam.SearchProviderType] = serpEvent.searchProviderType;
            params[InstrumentationParam.ItemCategory] = serpEvent.itemCategory;
            params[InstrumentationParam.SearchResultType] = serpEvent.searchResultType;
            params[InstrumentationParam.SearchSessionId] = this.searchSessionId;

            switch (serpEvent.itemCategory) {
                case SearchItemCategory.DataFetchLatency:
                    params[InstrumentationParam.Latency] = logProperties?.DataFetchLatency;
                    break;
                case SearchItemCategory.E2ELatency:
                    params[InstrumentationParam.Latency] = logProperties?.E2ELatency;
                    break;
            }

            if (serpEvent.itemCategory === SearchItemCategory.Result) {
                this.instrumentationService
                .logClickEvent(
                    serpEvent.event,
                    serpEvent.elementId,
                    serpEvent.knownArea.toString(),
                    referral ? referral : this.getReferralFromEventData(logProperties.eventData),
                    referralPlatform,
                    params,
                    undefined,
                    undefined,
                    serpEvent.pageNav);
                logUserAction({
                    id: serpEvent.elementId,
                    area: serpEvent.knownArea.toString() as Area,
                    result: referral ? 'Refer' : 'Navigate',
                    target: referral ? getReferral(referral) : 'Other'
                });
                // Put Clicks related properties to Feature event for Search team scorcarding
                params[InstrumentationParam.Area] = serpEvent.knownArea.toString();
                params[InstrumentationParam.ElementId] = serpEvent.elementId;
                params[InstrumentationParam.Referral] = referral ? referral : this.getReferralFromEventData(logProperties.eventData);
                params[InstrumentationParam.ReferralPlatform] = referralPlatform;
                logFeature({ featureName: KnownFeatureNames.Search , eventName: serpEvent.event, params});
            } else {
                params[InstrumentationParam.Area] = serpEvent.knownArea.toString();
                this.instrumentationService.logGenericEvent(
                    serpEvent.event,
                    params);
                logFeature({ featureName: KnownFeatureNames.Search , eventName: serpEvent.event, params});
            }
        });
    }

    public sentSearchTextLog(success: boolean, data?: any, securityThreshold?: DataSecurityLevel): void {
        const params = {};

        // this.searchText is a string container.
        // The length property of the container is considered System Metadata and is not a Direct Measurement of customer content so is permitted.
        params[InstrumentationParam.SearchCharacterCount] = this.searchText.length;
        params[InstrumentationParam.SearchSessionId] = this.searchSessionId;
        params[InstrumentationParam.SearchQueryId] = this.searchQueryId;
        params[InstrumentationParam.Action] = this.searchSubmitted ? KnownInstrumentationActions.Submit : null;

        if (success) {
            if (data !== undefined && data['Index'] !== undefined) {
                params[InstrumentationParam.SearchTargetId] = data['Index'];
            } else {
                params[InstrumentationParam.Error] = 'Returned data is undefined';
                params[InstrumentationParam.SearchTargetId] = -2;
            }
        } else {
            params[InstrumentationParam.Error] = 'Failed to get query index';
            params[InstrumentationParam.SearchTargetId] = -2;
        }

        this.instrumentationService.logGenericEvent(
            KnownInstrumentationEvents.SearchQuery,
            params,
            undefined /*metrics*/,
            securityThreshold);
        logFeature({ featureName: KnownFeatureNames.Search , eventName: KnownInstrumentationEvents.SearchQuery, params}, securityThreshold as unknown as OtelDataSecurityLevel);
    }

    public getSearchSessionId() {
        return this.searchSessionId;
    }

    public updateSearchSessionId() {
        this.searchSessionId = GuidGenerator.createGuid();
        this.searchQueryId = 0;
        this.searchSubmitted = false;
    }

    public updateSearchText(searchText: string) {
        this.searchText = searchText;
        this.logSearchTextInfo();
    }

    public setSearchReferralPlatform(isUwp: boolean) {
        this.referralPlatform = isUwp ? KnownReferralPlatform.Native : KnownReferralPlatform.Web;
    }

    public setSearchSubmittedAction() {
        this.searchSubmitted = true;
    }

    public getSearchTextIdPromise(): Promise<Object> {

        const requestHeader: Map<string, string> = new Map<string, string>();
        requestHeader.set('Content-Type', 'application/json');
        requestHeader.set('Accept', 'application/json');
        requestHeader.set('x-clientuseragent', 'OfficeHomeMSA');
        requestHeader.set('x-correlationid', this.searchSessionId);

        const requestBody = {
            Text: this.searchText,
            IsFileHash: false
        };

        const request: HttpRequestOptions = {
            url: this.officeInsightsServiceEndpoint,
            headers: requestHeader
        };

        return this.searchInstrumentedXhrService.sendIntrumentedPostRequest(
            KnownInstrumentationEvents.OfficeInsightsSearchSuggestionRequest,
            KnownRequestInstrumentationEvents.OfficeInsightsSearchSuggestionRequest,
            this.officeInsightsServiceEndpoint,
            JSON.stringify(requestBody),
            request
        );
    }

    public setRenderedProviderResults(searchProviderType: SearchResultType, results: Array<string>) {
        this.renderedSearchResults.set(searchProviderType, results);
    }

    private getReferralFromEventData(eventData: string): string {

        if (!eventData) {
            return '';
        }
        eventData = eventData.toLocaleLowerCase();

        if (eventData.indexOf('excel') !== -1) {
            return KnownWorkloadReferralNames.Excel;
        }

        if (eventData.indexOf('powerpoint') !== -1) {
            return KnownWorkloadReferralNames.PowerPoint;
        }

        if (eventData.indexOf('word') !== -1) {
            return KnownWorkloadReferralNames.Word;
        }

        if (eventData.indexOf('onenote') !== -1) {
            return KnownWorkloadReferralNames.OneNote;
        }

        if (eventData.indexOf('pdf') !== -1) {
            return OtherKnownReferralNames.PDF.toString();
        }

        return '';
    }
}
